import { Entity } from '$legacyecstatic';
import { Components } from 'editor/components';
import Cursor, { CursorTypes } from 'editor/components/Cursor';
import { MapboxEventArgs } from 'editor/components/map/MapboxEvents/mapboxEventHandlers';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import RFIPost from 'editor/components/RFIPost';
import {
  BoxControlPoint,
  boxControlPointHit,
  getBoxControlPointCursor,
  lineControlPointHit,
} from 'editor/utils/controlPoints';
import { getSheetLayerHits, LayerHit } from 'editor/utils/map';
import mapboxgl from 'mapbox-gl';

interface HitEntity {
  entity: Entity<Components>;
  layerHit: LayerHit;
}

const cursorEvents = {
  mousemove: (
    args: MapboxEventArgs<mapboxgl.MapEventType['mousemove']>
  ): void => {
    const { world, evt } = args;
    const { lng, lat } = evt.lngLat;

    const hitMarkups: HitEntity[] = [];
    const selectedHitMarkups: HitEntity[] = [];

    const cursorComp = world.grab(Cursor)?.component;
    if (!cursorComp) return;

    const layerHits = getSheetLayerHits(world, lng, lat);

    // Short circuit if no layer hits.
    if (layerHits.length === 0) {
      cursorComp.disabled = true;

      return;
    }

    // Assuming from this point on that there was layer hits.
    cursorComp.disabled = false;

    // Set up hit/selected markups for all following logic.
    // Keeping all logic that sets cursors outside of this to keep things a bit cleaner.
    layerHits.forEach((lh) => {
      const { hit } = lh;
      if (!hit) return;

      world.locateAll([Markup, MarkupKit]).forEach((ent) => {
        const m = ent.get(Markup);
        const mk = ent.get(MarkupKit);
        const point = new mk.MKPoint(hit.x, hit.y);
        const hitEntity = { entity: ent, layerHit: lh };

        if (!m.mkMarkup || m.hidden) {
          return;
        }

        if (m.mkMarkup.hit(point)) {
          hitMarkups.push(hitEntity);
        }

        if (m.selected) {
          selectedHitMarkups.push(hitEntity);
        }
      });
    });

    // The following code is all about setting the cursor type on the Cursor component.
    // It's all very control flow heavy and is mostly managed with branching "short-circuits" (return statements).
    // Basically whenever you are sure of what a cursor should be, set it on the component and "return;"!

    // See if any markups were "hit". This does not need to be sheet specific.
    if (hitMarkups.length > 0) {
      // Can't move punches at this time.
      const overPunch = hitMarkups.some(
        ({ entity }) => entity.get<Markup>(Markup).isPunch
      );

      const overRFI = hitMarkups.some(
        ({ entity }) => entity.get<Markup>(Markup).isRFI
      );

      if (overRFI) {
        cursorComp.cursor = CursorTypes.Pointer;

        const hasRFIPost = world.locate(RFIPost);

        if (hasRFIPost) {
          cursorComp.cursor = CursorTypes.Move; // NOTE: Would be nice to move this.
        }
      } else if (overPunch) {
        cursorComp.cursor = CursorTypes.Pointer;

        // Is the selected markup one of the hit markups?
        const selectedAndHitEntity = hitMarkups.find(({ entity }) => {
          const markupId = entity.get(Markup).markup.markupid;

          return selectedHitMarkups.some(({ entity: _entity }) => {
            const _markupId = _entity.get(Markup).markup.markupid;

            return _markupId === markupId;
          });
        });

        const m = selectedAndHitEntity?.entity.get(Markup);

        if (m && m.isPunch) {
          cursorComp.cursor = CursorTypes.Move;
        }
      }

      return;
    }

    // possibly over a control point.
    // This is a little weird since since we should only be able to select
    // one markup at a time right now, but this might change in the future.
    if (selectedHitMarkups.length > 0) {
      for (const { entity, layerHit } of selectedHitMarkups) {
        const { hit } = layerHit;
        if (!hit) return; // This would be a really weird state, and should never happen.

        const m = entity.get(Markup);
        const markupType = m.mkMarkup?.getType();

        switch (markupType) {
          case 'Square':
            {
              const cph = boxControlPointHit(m, hit);

              if (cph !== BoxControlPoint.None) {
                cursorComp.cursor = getBoxControlPointCursor(cph);

                return;
              }
            }
            break;
          case 'FreeText':
            {
              const cph = boxControlPointHit(m, hit);

              if (cph !== BoxControlPoint.None) {
                cursorComp.cursor = getBoxControlPointCursor(cph);

                return;
              }
            }
            break;
          case 'Line':
            {
              const cph = lineControlPointHit(m, hit);

              if (cph !== null) {
                cursorComp.cursor = CursorTypes.Pointer;

                return;
              }
            }
            break;
          default:
        }
      }
    }

    // If none of the above cases are true, then disable the cursor so that Mapbox can control it.
    cursorComp.disabled = true;
  },
};

export default cursorEvents;
